import React, { useState, useCallback, useEffect } from 'react';
import { Upload, Image, X } from 'lucide-react';
import { Button } from './ui/button';
import { filesApi } from '../services/api';
import { useToast } from '../hooks/use-toast';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif',
                       'video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];

export function FileUpload({ onUploadComplete, currentImage }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const [isVideo, setIsVideo] = useState(false);
  const { toast } = useToast();

  // Sync preview when currentImage prop changes (e.g. loaded from library or existing post)
  useEffect(() => {
    if (currentImage) {
      setPreview(currentImage);
    }
  }, [currentImage]);

  const convertHeicToJpeg = async (file) => {
    // Dynamic import of heic2any
    const heic2any = (await import('heic2any')).default;
    const blob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.8
    });
    return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
  };

  const handleFile = async (file) => {
    if (!file) return;

    // Check file type
    const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
                   file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
    const isVid = file.type.startsWith('video/') || 
                  /\.(mp4|mov|avi|webm)$/i.test(file.name);
    
    if (!ALLOWED_TYPES.includes(file.type) && !isHeic && !isVid) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image or video (JPG, PNG, GIF, WebP, HEIC, MP4, MOV)",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);
    setIsVideo(isVid);
    try {
      let fileToUpload = file;

      // Convert HEIC/HEIF to JPEG (server-side also converts, but this gives instant preview)
      if (isHeic) {
        toast({ title: "Converting HEIC...", description: "Please wait" });
        try {
          fileToUpload = await convertHeicToJpeg(file);
        } catch {
          // Let server handle conversion
          toast({ title: "Converting on server..." });
        }
      }

      // Create preview
      if (isVid) {
        setPreview(URL.createObjectURL(fileToUpload));
      } else {
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(fileToUpload);
      }

      // Upload
      const result = await filesApi.upload(fileToUpload);
      
      if (onUploadComplete) {
        onUploadComplete(result);
      }

      toast({
        title: "Upload successful",
        description: isVid ? "Video uploaded successfully" : "Image uploaded successfully"
      });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error.response?.data?.detail || "Failed to upload file",
        variant: "destructive"
      });
      setPreview(null);
      setIsVideo(false);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const clearPreview = () => {
    if (isVideo && preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setIsVideo(false);
    if (onUploadComplete) {
      onUploadComplete(null);
    }
  };

  return (
    <div data-testid="file-upload-section">
      {!preview ? (
        <div
          className={`upload-zone cursor-pointer ${isDragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('image-upload-input').click()}
          data-testid="image-drop-zone"
        >
          {isUploading ? (
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 border-3 border-[#c5d93d] border-t-transparent rounded-full animate-spin mb-2"></div>
              <p className="text-[#1a3a4a]/70">Uploading...</p>
            </div>
          ) : (
            <>
              <Image className="w-8 h-8 text-[#1a3a4a]/30 mx-auto mb-2" />
              <p className="text-sm text-[#1a3a4a]/70">Drop an image or video, or click to upload</p>
              <p className="text-xs text-[#1a3a4a]/40 mt-1">JPG, PNG, GIF, WebP, HEIC, MP4, MOV</p>
            </>
          )}
          <input
            id="image-upload-input"
            type="file"
            accept="image/*,video/mp4,video/quicktime,video/webm,.heic,.heif,.mov"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="image-file-input"
          />
        </div>
      ) : (
        <div className="relative" data-testid="image-preview">
          {isVideo ? (
            <video 
              src={preview} 
              controls
              className="w-full h-48 object-cover rounded-md bg-black"
            />
          ) : (
            <img 
              src={preview} 
              alt="Preview" 
              className="w-full h-48 object-cover rounded-md"
            />
          )}
          <Button
            variant="destructive"
            size="sm"
            onClick={clearPreview}
            className="absolute top-2 right-2"
            data-testid="remove-image-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
